/**
 * Financial Clearance Engine — Phase 3 (Timing-Aware)
 *
 * Stage-aware clearance model that evaluates obligations by timing:
 *   pre_schedule  — must be satisfied before scheduling
 *   pre_install   — must be satisfied before install day
 *   post_install  — due after project completion
 *
 * ADDITIVE ONLY — falls back to Phase 1 deposit logic when no PaymentPlan exists.
 */

import type { Job, PaymentPlan, FinancingDetails, PaymentLineItem, PaymentDueAt } from "./types";
import { derivePaymentStatus } from "./invoiceEngine";
import { deriveReadinessFromJobExcludingBuildReview } from "./readiness";

// ── Public Types ──

export type ClearanceStage = "pre_schedule" | "pre_install" | "post_install";
export type FinancialClearanceStatus = "blocked" | "pending" | "cleared";

export interface FinancialClearance {
  status: FinancialClearanceStatus;
  reason: string;
  details: ClearanceDetail[];
  /** The stage this clearance was evaluated for */
  stage: ClearanceStage;
  /** true if an authorized role has overridden the gate */
  overrideActive?: boolean;
}

export interface ClearanceDetail {
  label: string;
  met: boolean;
  explanation: string;
  /** Which stage this obligation belongs to */
  dueAt?: ClearanceStage;
}

// ── Timing Helpers ──

/** Resolve the effective dueAt for a line item, using sensible defaults */
function resolveDueAt(li: PaymentLineItem): PaymentDueAt {
  if (li.dueAt) return li.dueAt;
  switch (li.kind) {
    case "deposit": return "pre_schedule";
    case "progress": return "pre_install";
    case "final": return "post_install";
  }
}

function isSatisfied(li: PaymentLineItem): boolean {
  // For financed items, "approved" means the financing institution has committed to pay
  if (li.method === "financed" && li.status === "approved") return true;
  return li.status === "paid" || li.status === "waived";
}

function normalizeStatus(value: unknown): string {
  return String(value ?? "").trim().toLowerCase();
}

function isLegacyFinancingSelected(job: Job): boolean {
  const planType = String(job.financials?.paymentPlan?.type ?? "").toLowerCase();
  if (planType === "financed" || planType === "financing") return true;
  const contractPaymentMethod = String((job.contractDocument as { paymentMethod?: unknown } | undefined)?.paymentMethod ?? "").toLowerCase();
  return contractPaymentMethod.includes("financ");
}

function isContractFullyPaid(job: Job): boolean {
  const contractValue = Math.max(0, Number(job.financials?.contractValue ?? job.payoutAmount ?? 0));
  if (contractValue <= 0) return false;

  const invoices = job.financials?.invoices ?? [];
  const payments = job.financials?.payments ?? [];

const paidFromPayments = payments.reduce((sum, p) => sum + Math.max(0, Number(p.amount ?? 0)), 0);
   const paidFromInvoices = invoices.reduce((sum, inv) => {
     const status = normalizeStatus(inv.status);
     if (status !== "paid") return sum;
     return sum + Math.max(0, Number(inv.amount ?? 0));
  }, 0);

  const resolvedPaid = Math.max(paidFromPayments, paidFromInvoices);
  return resolvedPaid >= contractValue;
}

// ── Core Engine ──

/**
 * Evaluate financial clearance for a specific stage.
 * Default stage is "pre_schedule" for backward compatibility.
 */
export function deriveFinancialClearance(
  job: Job,
  stage: ClearanceStage = "pre_schedule"
): FinancialClearance {
  if (isContractFullyPaid(job)) {
    return {
      status: "cleared",
      reason: "Financially cleared — contract paid in full",
      details: [
        {
          label: "Payment",
          met: true,
          explanation: "Contract balance is fully paid",
          dueAt: stage,
        },
      ],
      stage,
    };
  }

  const plan = job.financials?.paymentPlan;
  const financing = job.financials?.financing;

  if (plan) {
    return evaluateStructuredPlan(syncDepositFromJobState(plan, job), financing, stage);
  }

  return evaluateLegacyDeposit(job, stage);
}

/**
 * Sync the deposit line item's status from the job's invoice/payment/legacy state.
 * The structured plan's deposit lineItem often stays "draft" after payment is
 * recorded through the invoice flow — this bridges the two representations.
 */
function syncDepositFromJobState(plan: PaymentPlan, job: Job): PaymentPlan {
  const depositItem = plan.lineItems.find(li => li.kind === "deposit");
  if (!depositItem || isSatisfied(depositItem)) return plan; // already satisfied — no sync needed

  const invoices = job.financials?.invoices ?? [];
  const payments = job.financials?.payments ?? [];

  // Check if any deposit-type invoice is marked paid
  const depositInvoicePaid = invoices.some(inv => {
    const type = String(inv.type ?? "").toLowerCase();
    const status = String(inv.status ?? "").toLowerCase();
    return (type === "deposit" || type === "down_payment") && status === "paid";
  });

  // Check if recorded payments cover the deposit amount (≥90% threshold for rounding)
  const totalPaid = payments.reduce((sum, p) => sum + Math.max(0, Number(p.amount ?? 0)), 0);
  const depositCoveredByPayments = depositItem.amount > 0 && totalPaid >= depositItem.amount * 0.9;

  // Check legacy paymentStatus field (set by older flow before plan existed)
  const legacyStatus = String(job.financials?.paymentStatus ?? "").toLowerCase();
  const legacyDepositReceived =
    legacyStatus === "deposit received" ||
    legacyStatus === "partial payment" ||
    legacyStatus === "paid" ||
    legacyStatus === "paid in full";

  if (depositInvoicePaid || depositCoveredByPayments || legacyDepositReceived) {
    return {
      ...plan,
      lineItems: plan.lineItems.map(li =>
        li.kind === "deposit" ? { ...li, status: "paid" as const } : li
      ),
    };
  }

  return plan;
}

// ── Structured Plan Evaluation ──

function evaluateStructuredPlan(
  plan: PaymentPlan,
  financing: FinancingDetails | undefined,
  stage: ClearanceStage
): FinancialClearance {
  const details: ClearanceDetail[] = [];
  let blocked = false;
  let pending = false;

  // ── 1. Deposit check ──
  if (plan.requiresDeposit) {
    const depositItem = plan.lineItems.find(li => li.kind === "deposit");
    if (depositItem) {
      const effectiveDue = resolveDueAt(depositItem);
      const met = isSatisfied(depositItem);
      const dueLabelMap: Record<PaymentDueAt, string> = {
        pre_schedule: "before scheduling",
        pre_install: "before installation",
        post_install: "at project completion",
      };
      details.push({
        label: "Deposit",
        met,
        explanation: met
          ? `Deposit of $${depositItem.amount.toLocaleString()} ${depositItem.status}`
          : `Deposit of $${depositItem.amount.toLocaleString()} due ${dueLabelMap[effectiveDue]}`,
        dueAt: effectiveDue,
      });
      // Only block/pend if this obligation is relevant to the current stage
      if (!met && isRelevantForStage(effectiveDue, stage)) {
        blocked = true;
      }
    }
  } else {
    details.push({ label: "Deposit", met: true, explanation: "No upfront payment required for this plan", dueAt: "pre_schedule" });
  }

  // ── 2. Financing check ──
  const financedItems = plan.lineItems.filter(li => li.method === "financed");
  if (financedItems.length > 0) {
    const fStatus = financing?.financingStatus ?? "not_applicable";
    const approved = fStatus === "approved";
    const declined = fStatus === "declined";

    details.push({
      label: "Financing",
      met: approved,
      explanation: approved
        ? `Financing approved: $${(financing?.financingApprovedAmount ?? 0).toLocaleString()}`
        : declined
          ? "Financing declined — alternative payment required"
          : fStatus === "pending"
            ? "Financing application pending approval"
            : "Financing approval required before scheduling",
      dueAt: "pre_schedule",
    });

    // Financing: approval is REQUIRED before scheduling for any financed deal
    // declined = hard block, pending = soft block, not_applicable = hard block
    if (!approved) {
      if (declined || fStatus === "not_applicable") blocked = true;
      else if (fStatus === "pending") pending = true;
    }
  }

  // ── 3. 0-down financing resolution ──
  // If financing is approved AND no pre_schedule obligations remain unpaid,
  // clear the deposit block (0-down scenario)
  if (!blocked && !pending) {
    // Already cleared, nothing to do
  } else if (financedItems.length > 0) {
    const fStatus = financing?.financingStatus ?? "not_applicable";
    if (fStatus === "approved") {
      // Check if total required pre_schedule amount is 0 or all satisfied
      const preSchedItems = plan.lineItems.filter(
        li => isRelevantForStage(resolveDueAt(li), "pre_schedule") && li.method !== "financed"
      );
      const unsatisfiedPreSched = preSchedItems.filter(li => !isSatisfied(li));
      const unsatisfiedAmount = unsatisfiedPreSched.reduce((s, li) => s + li.amount, 0);

      if (unsatisfiedAmount === 0) {
        // 0-down financing: financing approved + no cash/card pre_schedule obligations
        blocked = false;
        pending = false;
        // Update deposit detail to reflect 0-down cleared status
        const depositDetail = details.find(d => d.label === "Deposit");
        if (depositDetail && !depositDetail.met) {
          depositDetail.met = true;
          depositDetail.explanation = "No upfront payment required — financing approved";
        }
      }
    }
  }

  // ── 4. Customer cash/card obligations for this stage ──
  const cashCardItems = plan.lineItems.filter(
    li => (li.method === "cash" || li.method === "card") && li.kind !== "deposit"
  );
  const relevantItems = cashCardItems.filter(li => isRelevantForStage(resolveDueAt(li), stage));
  const requiredAmount = relevantItems.reduce((s, li) => s + li.amount, 0);

  if (requiredAmount > 0) {
    const paidAmount = relevantItems.filter(isSatisfied).reduce((s, li) => s + li.amount, 0);
    const met = paidAmount >= requiredAmount;
    const dueLabelMap: Record<PaymentDueAt, string> = {
      pre_schedule: "before scheduling",
      pre_install: "before installation",
      post_install: "at project completion",
    };
    details.push({
      label: "Customer Portion",
      met,
      explanation: met
        ? `Customer portion of $${requiredAmount.toLocaleString()} satisfied`
        : `$${(requiredAmount - paidAmount).toLocaleString()} due ${dueLabelMap[stage]}`,
      dueAt: stage,
    });
    if (!met) {
      // pre_schedule obligations are blocking; others are pending
      if (stage === "pre_schedule") blocked = true;
      else pending = true;
    }
  }

  // ── Derive overall status ──
  const status: FinancialClearanceStatus = blocked ? "blocked" : pending ? "pending" : "cleared";
  const reason = status === "cleared"
    ? deriveSmartClearanceReason(plan, financing)
    : details.filter(d => !d.met).map(d => d.explanation).join("; ");

  return { status, reason, details, stage };
}

/** Generate a context-aware clearance reason */
function deriveSmartClearanceReason(plan: PaymentPlan, financing: FinancingDetails | undefined): string {
  const fStatus = financing?.financingStatus;
  const hasFinancedItems = plan.lineItems.some(li => li.method === "financed");

  if (hasFinancedItems && fStatus === "approved" && !plan.requiresDeposit) {
    return "Financial clearance satisfied — no upfront payment required (financing approved)";
  }
  if (hasFinancedItems && fStatus === "approved") {
    const dep = plan.lineItems.find(li => li.kind === "deposit");
    if (dep && dep.amount === 0) {
      return "Financial clearance satisfied — 0-down financing approved";
    }
    return "Financing approved — all financial requirements satisfied";
  }
  return "All financial requirements satisfied";
}

// ── Legacy Deposit Fallback ──

function evaluateLegacyDeposit(job: Job, stage: ClearanceStage): FinancialClearance {
  const legacyFinancing = isLegacyFinancingSelected(job);
  const readiness = deriveReadinessFromJobExcludingBuildReview(job);
  const paymentStatus = derivePaymentStatus(job);
  const rawStatus = job.financials?.paymentStatus ?? "";
  const details: ClearanceDetail[] = [];
  const invoices = job.financials?.invoices ?? [];

  if (invoices.length === 0) {
    if (legacyFinancing) {
      const financingStatus = String(job.financials?.financing?.financingStatus ?? "pending").toLowerCase();
      const financingApproved = financingStatus === "approved";
      const financingRejected = financingStatus === "declined" || financingStatus === "rejected";
      details.push({
        label: "Financing",
        met: financingApproved,
        explanation: financingApproved
          ? "Financing approved — no deposit invoice required before scheduling"
          : financingRejected
            ? "Financing rejected — scheduling blocked until alternate payment is configured"
            : "Financing approval pending — scheduling blocked until financing is approved",
        dueAt: "pre_schedule",
      });
      return {
        status: financingApproved ? "cleared" : "blocked",
        reason: financingApproved
          ? "Financial clearance satisfied — financing approved (deposit not required)"
          : financingRejected
            ? "Financially blocked — financing rejected"
            : "Financially blocked — financing approval pending",
        details,
        stage,
      };
    }
    // Check the raw paymentStatus field — operators may record a deposit payment
    // through the Financials panel without generating formal invoices yet.
    const rawStatus = job.financials?.paymentStatus ?? "";
    const depositReceived =
      rawStatus === "Deposit Received" ||
      rawStatus === "Partial Payment" ||
      rawStatus === "Paid" ||
      rawStatus === "Paid in Full";
    if (depositReceived) {
      details.push({
        label: "Deposit",
        met: true,
        explanation: `Payment recorded: ${rawStatus}`,
        dueAt: "pre_schedule",
      });
      return { status: "cleared", reason: `Deposit recorded (${rawStatus}) — cleared to proceed`, details, stage };
    }
    details.push({
      label: "Deposit",
      met: false,
      explanation: "No invoice plan generated yet — payment workflow not started",
      dueAt: "pre_schedule",
    });
    return {
      status: "blocked",
      reason: "No invoice plan generated yet — financial workflow blocked until invoicing is created",
      details,
      stage,
    };
  }

  const depositPaid =
    readiness.financial.state === "deposit_received" ||
    readiness.financial.state === "paid" ||
    readiness.financial.state === "paid_in_full" ||
    paymentStatus === "deposit_paid" ||
    paymentStatus === "paid_in_full";
  const paidInFull =
    readiness.financial.state === "paid" ||
    readiness.financial.state === "paid_in_full" ||
    paymentStatus === "paid_in_full";

  if (stage === "post_install" && !paidInFull) {
    details.push({
      label: "Final Payment",
      met: false,
      explanation: "Final payment is pending before project closeout",
      dueAt: "post_install",
    });
    return {
      status: "blocked",
      reason: "Final payment is pending before project closeout",
      details,
      stage,
    };
  }

  details.push({
    label: "Deposit",
    met: depositPaid,
    explanation: depositPaid
      ? "Financially cleared"
      : "Financial clearance required — payment due before scheduling",
    dueAt: "pre_schedule",
  });

  const status: FinancialClearanceStatus = depositPaid ? "cleared" : "blocked";
  const reason = depositPaid
    ? "Financially cleared — all obligations satisfied"
    : "Financial clearance required — payment due before scheduling";

  return { status, reason, details, stage };
}

// ── Stage Relevance ──

/** Returns true if an obligation's dueAt is relevant for the given evaluation stage */
function isRelevantForStage(dueAt: PaymentDueAt, stage: ClearanceStage): boolean {
  const order: Record<string, number> = { pre_schedule: 0, pre_install: 1, post_install: 2 };
  return order[dueAt] <= order[stage];
}

// ── UI Helpers ──

export function getClearanceLabel(status: FinancialClearanceStatus): string {
  switch (status) {
    case "cleared": return "Financially Cleared";
    case "pending": return "Clearance Pending";
    case "blocked": return "Financially Blocked";
  }
}

export function getClearanceColor(status: FinancialClearanceStatus): string {
  switch (status) {
    case "cleared": return "text-success";
    case "pending": return "text-warning";
    case "blocked": return "text-destructive";
  }
}

/** Human-readable summary of what the customer owes and why, with timing context */
export function getCustomerPaymentSummary(job: Job): string[] {
  const plan = job.financials?.paymentPlan;
  const financing = job.financials?.financing;
  const messages: string[] = [];

  if (plan) {
    // ── Deposit messaging ──
    const hasFinancing = financing?.financingStatus === "approved";
    if (plan.requiresDeposit) {
      const dep = plan.lineItems.find(li => li.kind === "deposit");
      if (dep && !isSatisfied(dep)) {
        // 0-down financing: deposit not required if financing approved and amount is 0
        if (hasFinancing && dep.amount === 0) {
          messages.push("No payment required to begin — financing approved ✓");
        } else {
          const timing = resolveDueAt(dep);
          const timingLabel = timing === "pre_schedule" ? "before scheduling"
            : timing === "pre_install" ? "before installation" : "at project completion";
          messages.push(`$${dep.amount.toLocaleString()} deposit due ${timingLabel}`);
        }
      } else if (dep) {
        messages.push("Deposit received ✓");
      }
    } else {
      if (hasFinancing) {
        messages.push("No payment required to begin — financing approved ✓");
      } else {
        messages.push("No upfront payment required");
      }
    }

    // ── Financing messaging ──
    if (financing?.financingStatus === "approved") {
      messages.push(`Financing approved — $${(financing.financingApprovedAmount ?? 0).toLocaleString()}`);
    } else if (financing?.financingStatus === "pending") {
      messages.push("Financing application under review");
    } else if (financing?.financingStatus === "declined") {
      messages.push("Financing declined — please contact us for alternatives");
    }

    // ── Remaining obligations by timing ──
    const pendingItems = plan.lineItems.filter(li => !isSatisfied(li) && li.kind !== "deposit");
    const byTiming: Record<string, number> = {};
    for (const li of pendingItems) {
      const t = resolveDueAt(li);
      byTiming[t] = (byTiming[t] ?? 0) + li.amount;
    }
    const timingLabels: Record<string, string> = {
      pre_schedule: "due before scheduling",
      pre_install: "due before installation",
      post_install: "due at project completion",
    };
    for (const [timing, amount] of Object.entries(byTiming)) {
      messages.push(`$${amount.toLocaleString()} ${timingLabels[timing] ?? "remaining"}`);
    }

    if (plan.lineItems.every(isSatisfied)) {
      messages.push("Paid in full ✓");
    }
  } else {
    // Legacy fallback
    const invoices = job.financials?.invoices ?? [];
    const unpaid = invoices.filter(i => i.status !== "paid");
    if (unpaid.length > 0) {
      unpaid.forEach(inv => {
        messages.push(`${inv.type.charAt(0).toUpperCase() + inv.type.slice(1)}: $${inv.amount.toLocaleString()} ${inv.status === "sent" ? "due now" : inv.status}`);
      });
    } else if (invoices.length > 0) {
      messages.push("All invoices paid ✓");
    } else {
      messages.push("Invoice will be issued shortly");
    }
  }

  return messages;
}
